import { Button, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useUser, useUserPermissions } from '@paintscout/ui-business'
import { track } from 'shared/util/analytics'
import AddBoxIcon from '@material-ui/icons/AddBox'
import ArchiveIcon from '@material-ui/icons/Archive'
import EditIcon from '@material-ui/icons/Edit'
import CopyIcon from '@material-ui/icons/FileCopy'
import ListIcon from '@material-ui/icons/List'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'
import React, { useMemo } from 'react'
import type { RenderableItem } from '@paintscout/util/builder'
import { getItems, getObjectLabels, getQuoteOptions, getFeatures, setQuoteOptions } from '@paintscout/util/builder'
import get from 'lodash/get'
import { hasCustomTotal } from '@paintscout/util/'
import { canSaveItemToSettings, saveToSettingsAction } from '../util/saveToSettingsAction'
import { useQuote } from '../context'
import AmountHeaderLabel from './AmountHeaderLabel'
import AreaSubstrateToggle from './AreaSubstrateToggle'
import HideAmountButton from './HideAmountButton'
import ItemRowContent from './ItemRowContent'
import OutlinedItemTable from './OutlinedItemTable'
import type { OutlinedItemTableProps } from './OutlinedItemTable'

export interface BidTableNextProps {
  selectedItems?: OutlinedItemTableProps['selectedItems']
  onItemSelection?: OutlinedItemTableProps['onItemSelection']
  onDeselectItem?: (args: {
    section: 'bid' | 'options' | 'additional' | 'pending' | 'archived' | 'all'
    key?: string
  }) => void
  onItemAction?: (action: string, section: 'bid', item: RenderableItem) => void
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {},
    // Toolbar above the card. When Swap Products is available (no selection), the Area/Surface view
    // dropdown sits on the left and Swap Products on the right; otherwise the view dropdown is
    // right-aligned on its own. (Once items are selected, swap lives in the bulk-actions bar instead.)
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
      minHeight: 32
    },
    swapButton: {
      fontWeight: 400,
      fontSize: 14,
      color: theme.paintscout.palette.brand.Neutral.P35,
      '& .hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96
      }
    }
  }),
  { name: 'BidTableNext' }
)

/**
 * The "Item" (bid) section for the new (estimateNext) quote pipeline, rendered in the outlined
 * card style. Ported from `BidTable` — same item building, action arrays, amount toggle, and
 * Area/Surface toggle — but rendered through `OutlinedItemTable`, reusing `ItemColumn` for the row
 * content and `ActionColumn` for the per-row kebab. The Area/Surface toggle sits above the card and
 * the Hide-amount switch lives in the card header.
 */
export default function BidTableNext({
  onItemAction,
  onDeselectItem,
  selectedItems,
  onItemSelection
}: BidTableNextProps) {
  const classes = useStyles()
  const {
    quote,
    tableView,
    isEditable,
    isNoteEditable,
    onReorder,
    onTableViewChange,
    onAddItem,
    updateQuote,
    onItemAction: contextItemAction,
    selectedItemKeys = []
  } = useQuote()
  const { options } = useClientOptions()
  const { isCrew } = useUser()
  const { permissionToWriteAllDocuments, permissionToEditAccountSettings } = useUserPermissions()
  const isInvoice = !!quote.is_invoice

  const features = getFeatures({ options })
  const hasAdditionalWork = get(features, 'quotes.additionalWork')
  const canConvertToLineItem = get(features, 'quotes.convertToLineItem')
  const canFullyEdit = isEditable || !quote.is_invoice || permissionToWriteAllDocuments || !hasAdditionalWork
  const { showPrices, showDetailedBreakdown, allowShowCustomizedTotalPrice, addIndex, hideAllPrices, serviceFees } =
    getQuoteOptions({ quote, options })

  const objectLabels = getObjectLabels({ options, invoice: isInvoice })
  const disableSubstrateView = hasCustomTotal(quote.areas)
  const items: RenderableItem[] = useMemo(
    () =>
      getItems({ quote, section: 'bid', view: tableView, consumer: 'customer', options }).map((item) => {
        if (showDetailedBreakdown && !item.description && !item.value && item.subItems && item.subItems.length > 0) {
          const firstSubItem = item.subItems.shift()
          return {
            ...item,
            description: { useCustom: false, default: firstSubItem.description, custom: '' },
            value: firstSubItem.value
          }
        }
        return item
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quote, tableView]
  )

  if (items.length === 0 && !isEditable) {
    return null
  }

  // Both toolbar controls act on existing items, so neither belongs on an estimate that has none —
  // a new estimate shows the item card and nothing else (ENG-1478).
  const hasItems = items.length > 0
  const showSwap = isEditable && selectedItemKeys.length === 0 && hasItems

  const allowAmountToggle =
    !quote.totalAdjustment ||
    allowShowCustomizedTotalPrice ||
    (quote.totalAdjustment && quote.totalAdjustment.rate === 0)
  const activeServiceFee = serviceFees?.hiddenValue && serviceFees.hiddenValue > 0
  const amountToggleDisabledReason = activeServiceFee
    ? 'A service fee is included in the sub-total, so prices for each item cannot be shown when using it.'
    : !allowAmountToggle
      ? 'Prices for each item cannot be shown because the total price has been customized.'
      : undefined

  function handleReorder({ items, addIndex }) {
    onReorder({ items, section: 'bid', view: tableView, addIndex })
  }

  function handleTableViewChange(_event: any, view: string) {
    onTableViewChange(view)
    if (onDeselectItem) {
      onDeselectItem({ section: 'all' })
    }
  }

  function handleToggleAmount(_ev: any) {
    const quoteOptions = getQuoteOptions({ options, quote })
    quoteOptions.showPrices = !quoteOptions.showPrices
    const updatedQuote = setQuoteOptions({ quoteOptions, quote })
    updateQuote({ quote: updatedQuote })
  }

  // Right side of the navy "Item" band: the amount toggle in the editor, and — for the customer, who
  // has nothing to toggle — the currency symbol over the price column, matching the legacy `BidTable`
  // head (AmountHeadColumn with `consumer: 'customer'` and no switch).
  const headerRight =
    items.length === 0 || hideAllPrices ? undefined : isEditable ? (
      <HideAmountButton
        onDark
        visibleLabel="Hide"
        hiddenLabel="Show"
        disabled={!allowAmountToggle || activeServiceFee}
        disabledTooltip={amountToggleDisabledReason}
        onToggle={handleToggleAmount}
        visible={showPrices && !hideAllPrices}
      />
    ) : showPrices ? (
      <AmountHeaderLabel onDark>{objectLabels.currency.symbol}</AmountHeaderLabel>
    ) : undefined

  return (
    <div className={classes.root}>
      {isEditable && hasItems && (
        <div className={classes.toolbar} style={{ justifyContent: showSwap ? 'space-between' : 'flex-end' }}>
          <AreaSubstrateToggle view={tableView} onChange={handleTableViewChange} disabled={disableSubstrateView} />
          {showSwap && (
            <Button
              className={classes.swapButton}
              variant="text"
              size="small"
              icon={SwapHorizIcon}
              onClick={() => {
                track('product_swap_opened', { source: 'bid_toolbar' })
                contextItemAction('swap-products', 'bid', [])
              }}
            >
              Swap Products
            </Button>
          )}
        </div>
      )}

      <OutlinedItemTable
        title={'Item'}
        headerRight={headerRight}
        headerVariant="dark"
        draggable={!!isEditable}
        checkboxes={isEditable}
        showAddButton={isEditable}
        items={items}
        selectedItems={selectedItems}
        addIndex={addIndex.bid}
        addButtonLabel={'Add Area or Line Item'}
        section={'bid'}
        onReorder={handleReorder}
        onItemSelection={onItemSelection}
        onItemClick={
          isEditable || isNoteEditable ? (_ev, item) => onItemAction('edit', 'bid', item as RenderableItem) : undefined
        }
        singleBorder
        onAddClick={(_ev, insertAfterKey, type) => onAddItem('bid', insertAfterKey, type)}
        renderRowColumns={(item: RenderableItem) => {
          const actions = []

          actions.push({ key: 'edit', label: 'Edit', icon: EditIcon })

          if (canFullyEdit) {
            actions.push({ key: 'make-option', label: `Make ${objectLabels.option.value}`, icon: AddBoxIcon })
            actions.push({ key: 'archive', label: 'Archive', icon: ArchiveIcon })

            if (tableView === 'area') {
              actions.push({ key: 'copy', label: 'Duplicate', icon: CopyIcon })
            }
            if (item.type === 'group') {
              actions.push({ key: 'ungroup', label: 'Ungroup', icon: ListIcon })
            }
            if (canConvertToLineItem && item.type !== 'line_item') {
              actions.push({ key: 'convert', label: 'Convert to Line Item', icon: SwapHorizIcon })
            }

            if (canSaveItemToSettings({ permissionToEditAccountSettings, item })) {
              actions.push(saveToSettingsAction)
            }
          }

          return (
            <ItemRowContent
              item={item}
              section={'bid'}
              isEditable={isEditable || isNoteEditable}
              showAmount={showPrices}
              actions={isEditable && !isCrew ? actions : undefined}
              onAction={(action, it) => onItemAction(action, 'bid', it)}
            />
          )
        }}
      />
    </div>
  )
}
